import {
  CheckCircle2,
  KeyRound,
  Lock,
  LogOut,
  Mail,
  MoreHorizontal,
  ShieldCheck,
  ShieldOff,
  Sparkles,
  UserMinus,
  Unlock,
} from 'lucide-react';
import { motion } from 'motion/react';
import { DropdownMenu } from 'radix-ui';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMe, usePeople, usePersonAction, useSession, type PersonAction } from '../auth/session';
import { Skeleton } from '../components/feedback/Progress';
import { toastPromise } from '../components/feedback/toast';
import { Button } from '../components/ui/Button';
import { Badge, Card } from '../components/ui/Card';
import { Dialog } from '../components/ui/Dialog';
import type { AccountState, Person } from '../lib/api';
import { cn } from '../lib/cn';

// User console (docs/architecture.md §9b): who has access here, how secure their account is,
// and the admin actions — unlock (forces a password reset), sign out everywhere, remove.

const STATE_STYLE: Record<AccountState, { icon: ReactNode; className: string }> = {
  active: { icon: <CheckCircle2 size={13} />, className: 'bg-success/12 text-success' },
  invited: { icon: <Mail size={13} />, className: 'bg-info/12 text-info' },
  reset_required: { icon: <KeyRound size={13} />, className: 'bg-warning/12 text-warning' },
  locked: { icon: <Lock size={13} />, className: 'bg-danger/12 text-danger' },
  unknown: { icon: null, className: 'bg-subtle text-muted' },
};

export function PeoplePage() {
  const { t } = useTranslation();
  const { org, membership } = useSession();
  const { data: me } = useMe();
  const people = usePeople(org);
  const act = usePersonAction(org);
  const [removing, setRemoving] = useState<Person | null>(null);
  const canManage = membership?.role === 'owner' || membership?.role === 'admin';

  const run = (action: PersonAction, person: Person) => {
    const name = person.displayName;
    const messages = {
      unlock: {
        loading: t('people.toast.unlocking', { name }),
        success: t('people.toast.unlocked', { name }),
        description: t('people.toast.unlockedBody'),
      },
      signOut: {
        loading: t('people.toast.signingOut', { name }),
        success: t('people.toast.signedOut', { name }),
      },
      remove: {
        loading: t('people.toast.removing', { name }),
        success: t('people.toast.removed', { name }),
      },
    }[action];
    return toastPromise(act.mutateAsync({ action, userId: person.id }), messages).catch(
      () => undefined,
    );
  };

  return (
    <div className="mx-auto flex max-w-[1600px] flex-col gap-6">
      <header>
        <h1 className="text-3xl font-extrabold text-text">{t('people.title')}</h1>
        <p className="mt-1 max-w-2xl text-md text-muted">{t('people.subtitle')}</p>
      </header>

      <Card className="overflow-hidden">
        <div className="hidden grid-cols-[minmax(0,2.2fr)_minmax(0,1.6fr)_minmax(0,0.8fr)_minmax(0,1.4fr)_44px] gap-4 border-b border-border px-5 py-3 text-xs font-bold uppercase tracking-wider text-muted lg:grid">
          <span>{t('people.columns.person')}</span>
          <span>{t('people.columns.access')}</span>
          <span>{t('people.columns.security')}</span>
          <span>{t('people.columns.account')}</span>
          <span />
        </div>

        {people.isLoading ? (
          <div className="flex flex-col gap-2 p-4">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-16" />
            ))}
          </div>
        ) : !people.data?.length ? (
          <p className="p-10 text-center text-muted">{t('people.empty')}</p>
        ) : (
          <ul>
            {people.data.map((person, i) => {
              const isMe = person.id === me?.user.id;
              const style = STATE_STYLE[person.account.state];
              const locked = person.account.state === 'locked';
              return (
                <motion.li
                  key={person.id}
                  layout
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className={cn(
                    'grid grid-cols-[minmax(0,1fr)_44px] items-center gap-x-4 gap-y-2 border-b border-border px-5 py-4 last:border-b-0 lg:grid-cols-[minmax(0,2.2fr)_minmax(0,1.6fr)_minmax(0,0.8fr)_minmax(0,1.4fr)_44px]',
                    locked && 'bg-danger/[0.04]',
                  )}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-gradient-to-br from-primary to-glow-2 text-sm font-bold text-on-primary">
                      {initials(person.displayName)}
                    </span>
                    <span className="min-w-0">
                      <span className="flex flex-wrap items-center gap-1.5">
                        <span className="truncate font-semibold text-text">
                          {person.displayName}
                        </span>
                        {isMe && <Badge tone="accent">{t('people.you')}</Badge>}
                        {person.isPlatformStaff && (
                          <Badge>
                            <Sparkles size={10} /> {t('people.staff')}
                          </Badge>
                        )}
                      </span>
                      <span className="block truncate text-sm text-muted">{person.email}</span>
                    </span>
                  </div>

                  <div className="col-span-2 flex min-w-0 flex-wrap gap-1.5 lg:col-span-1">
                    {person.memberships.map((m) => (
                      <span
                        key={m.orgUnitId}
                        className="truncate rounded-md bg-subtle px-2 py-1 text-xs text-text"
                      >
                        <b>{t(`roles.${m.role}`, { defaultValue: m.role })}</b> · {m.orgUnitName}
                      </span>
                    ))}
                  </div>

                  <div className="col-span-2 flex items-center gap-1.5 text-sm lg:col-span-1">
                    {person.mfaEnrolled ? (
                      <ShieldCheck size={16} className="text-success" />
                    ) : (
                      <ShieldOff size={16} className="text-warning" />
                    )}
                    <span className={person.mfaEnrolled ? 'text-text' : 'text-muted'}>
                      {person.mfaEnrolled ? t('people.mfaOn') : t('people.mfaOff')}
                    </span>
                  </div>

                  <div className="col-span-2 flex flex-wrap items-center gap-x-2 gap-y-1 lg:col-span-1 lg:flex-col lg:items-start">
                    <span
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold',
                        style.className,
                      )}
                    >
                      {locked ? (
                        <motion.span
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ repeat: Infinity, duration: 1.8 }}
                        >
                          {style.icon}
                        </motion.span>
                      ) : (
                        style.icon
                      )}
                      {t(`people.states.${person.account.state}`)}
                    </span>
                    {person.account.failedSignIns > 0 && (
                      <span className="text-xs text-muted">
                        {t('people.failedSignIns', { count: person.account.failedSignIns })}
                      </span>
                    )}
                  </div>

                  <div className="row-start-1 col-start-2 justify-self-end lg:row-auto lg:col-auto">
                    {canManage && !isMe && (
                      <DropdownMenu.Root>
                        <DropdownMenu.Trigger asChild>
                          <button
                            aria-label={t('people.actions.menu', { name: person.displayName })}
                            className="grid h-9 w-9 place-items-center rounded-md text-muted transition-colors hover:bg-subtle hover:text-text"
                          >
                            <MoreHorizontal size={18} />
                          </button>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Portal>
                          <DropdownMenu.Content
                            align="end"
                            sideOffset={6}
                            className="glass z-50 min-w-64 rounded-lg p-1.5 elevated"
                          >
                            {(locked || person.account.state === 'reset_required') && (
                              <Item
                                icon={<Unlock size={16} />}
                                onSelect={() => void run('unlock', person)}
                              >
                                {t('people.actions.unlock')}
                              </Item>
                            )}
                            <Item
                              icon={<LogOut size={16} />}
                              onSelect={() => void run('signOut', person)}
                            >
                              {t('people.actions.signOut')}
                            </Item>
                            <DropdownMenu.Separator className="my-1 h-px bg-border" />
                            <Item
                              icon={<UserMinus size={16} />}
                              danger
                              onSelect={() => setRemoving(person)}
                            >
                              {t('people.actions.remove')}
                            </Item>
                          </DropdownMenu.Content>
                        </DropdownMenu.Portal>
                      </DropdownMenu.Root>
                    )}
                  </div>
                </motion.li>
              );
            })}
          </ul>
        )}
      </Card>

      <Dialog
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        title={t('people.remove.title', { name: removing?.displayName ?? '' })}
        description={t('people.remove.body')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setRemoving(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              loading={act.isPending}
              onClick={() => {
                if (removing) void run('remove', removing).then(() => setRemoving(null));
              }}
            >
              {t('people.remove.confirm')}
            </Button>
          </>
        }
      >
        {null}
      </Dialog>
    </div>
  );
}

function Item({
  icon,
  children,
  onSelect,
  danger,
}: {
  icon: ReactNode;
  children: ReactNode;
  onSelect: () => void;
  danger?: boolean;
}) {
  return (
    <DropdownMenu.Item
      onSelect={onSelect}
      className={cn(
        'flex cursor-pointer items-center gap-2.5 rounded-md px-3 py-2 text-base outline-none data-[highlighted]:bg-subtle',
        danger ? 'text-danger' : 'text-text',
      )}
    >
      <span className={danger ? 'text-danger' : 'text-muted'}>{icon}</span>
      {children}
    </DropdownMenu.Item>
  );
}

function initials(name: string): string {
  return name
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}
